/*  MUI  */
import { Alert, Button, Checkbox, Grid, TableCell } from '@mui/material';

/*  Shared  */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { DateFormat } from 'application/Shared/Components/Core';
import { ManagedTable } from 'application/Shared/Components/Table';
import { Usage } from 'application/Shared/Components/Usage';
import { Response, Route } from 'application/Shared/Helpers';
import { Requests } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';
import i18n from 'i18n';

/*  Object  */
import { Application } from 'objects/Application';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { withRouter } from 'router/withRouter';

const headers = [
    {
        align: 'left',
        field: 'name',
        renderHeader: <strong>{i18n.t('Common.application')}</strong>,
        width: '30%'
    },
    {
        align: 'center',
        field: null,
        renderHeader: <strong>{i18n.t('Common.usage')}</strong>,
        width: '20%'
    },
    {
        align: 'center',
        field: 'expertise',
        renderHeader: (
            <strong>
                {i18n.t('ManageApplicationAdmin.add_application_admin')}
            </strong>
        ),
        width: '20%'
    },
    {
        align: 'left',
        field: 'dateAdded',
        renderHeader: <strong>{i18n.t('Common.dateAdded')}</strong>,
        width: '20%'
    },
    {
        align: 'right',
        field: 'actions',
        renderHeader: <strong>{i18n.t('Common.actions')}</strong>,
        width: '20%'
    }
];

const parameters = {
    classified: true,
    includeApplication: true
};

const searchFields = [
    'applicationId',
    'application.name',
    'application.category',
    'application.subCategory'
];

class UserApps extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loaded: false,
            rerender: false,
            route: '/home/reports',
            userApplicationLinks: {}
        };

        /* requests */
        this.requests = new Requests();
    }

    componentDidMount() {
        const { match } = this.props;
        const { clientId, partnerId } = match.params;
        const route = Route.buildBaseReportsRoute({ clientId, partnerId });
        this.setState({ route }, () => {
            this.load();
        });
    }

    componentWillUnmount() {
        this.requests.cancel();
    }

    load = async () => {
        const { match, userId } = this.props;
        const { clientId } = match.params;

        const params = { platformUserId: userId, size: -1 };
        if (clientId) {
            params.platformTenantId = clientId;
            params.includeClients = true;
        }

        try {
            const response = await axios.get('/userApp', {
                cancelToken: this.requests.token(),
                params
            });
            if (!Response.hasData(response)) {
                debug.w('No User App Links found', response);
                this.setState({ loaded: true });
                return;
            }

            const data = Response.getData(response);

            const userApplicationLinks = {};
            data.forEach((appLink) => {
                const { applicationId } = appLink;
                userApplicationLinks[applicationId] = appLink;
            });

            this.setState({ loaded: true, userApplicationLinks });
        } catch (e) {
            debug.e('Unable to load User App Links', e);
        }
    };

    updateAdmin = async (applicationId, admin) => {
        const { match, userId } = this.props;
        const { clientId } = match.params;

        const { userApplicationLinks } = this.state;

        const params = { admin, platformUserId: userId };
        if (clientId) {
            params.platformTenantId = clientId;
        }

        try {
            await axios.put(
                `/userApp/${userApplicationLinks[applicationId].id}`,
                params
            );
            this.setState({
                userApplicationLinks: {
                    ...userApplicationLinks,
                    [applicationId]: {
                        ...userApplicationLinks[applicationId],
                        admin
                    }
                }
            });
        } catch (e) {
            /* */
        }
    };

    renderRow = (row) => {
        const { userId } = this.props;
        const { userApplicationLinks, route } = this.state;
        const { applicationId } = row;

        const appLink = userApplicationLinks[applicationId];

        return (
            <Application data={row.application} nameHasLink>
                <TableCell>
                    <ApplicationNameLinkCell withCategory />
                </TableCell>
                <TableCell align="center">
                    {appLink && (
                        <Usage
                            usageChart={appLink.usageChart}
                            usage={appLink.usage}
                            sx={{ maxHeight: '40px' }}
                        />
                    )}
                </TableCell>
                <TableCell align="center">
                    <Checkbox
                        disabled={!appLink}
                        checked={appLink && appLink.admin}
                        onChange={(e) =>
                            this.updateAdmin(
                                row.applicationId,
                                e.target.checked
                            )
                        }
                    />
                </TableCell>
                <TableCell>
                    <DateFormat date={appLink && appLink.dateCreated} />
                </TableCell>
                <TableCell>
                    {appLink ? (
                        <Link
                            to={`${route}?application=${applicationId}&userSelection=${userId}&period=monthly&reportType=organizational&run=true`}
                            data-trackid="admin_user_userAdmin_applications_viewReports_routeTo"
                        >
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                data-trackid="admin_user_userAdmin_applications_viewReports"
                            >
                                View Reports
                            </Button>
                        </Link>
                    ) : (
                        <>-</>
                    )}
                </TableCell>
            </Application>
        );
    };

    renderTable = () => {
        const { rerender } = this.state;

        return (
            <ManagedTable
                headers={headers}
                parameters={parameters}
                renderBody={this.renderRow}
                renderBodyKey="id"
                renderFooter
                rerender={rerender}
                rowsApi="/tenantApp"
                rowsApiPreload
                rowsPerPage={10}
                searchEnabled
                searchFields={searchFields}
                sortDirection="DESC"
                sortFieldType="string"
                sortKey="application.name"
            />
        );
    };

    render() {
        const { t } = this.props;
        const { loaded } = this.state;

        return (
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Alert severity="info" icon={false}>
                        {t('UserAdminAplications.description')}
                    </Alert>
                    {loaded && this.renderTable()}
                </Grid>
            </Grid>
        );
    }
}
export default withRouter(withTranslation()(UserApps));
